import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Digite seu e-mail no campo acima para receber o link de redefinição.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/redefinir-senha`,
      });
      if (resetError) {
        setError("Não foi possível enviar o e-mail agora. Tente novamente em alguns minutos.");
      } else {
        toast.success("Se este e-mail estiver cadastrado, você receberá um link para redefinir a senha.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError("E-mail ou senha incorretos");
        return;
      }

      const { data: userData } = await supabase
        .from("usuarios")
        .select("status")
        .eq("id", authData.user.id)
        .maybeSingle();

      if (userData?.status === "bloqueado") {
        await supabase.auth.signOut();
        setError("Acesso bloqueado. Entre em contato com o administrador.");
        return;
      }

      toast.success("Login realizado com sucesso!");
      navigate({ to: "/" });
    } catch (err) {
      setError("Ocorreu um erro ao tentar entrar");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-foreground tracking-tight">
          Studio <span className="text-primary">Tattoo</span>
        </h1>
      </div>

      <Card className="w-full max-w-md bg-card border-border shadow-sm">
        <CardHeader className="pt-8 pb-4 text-center">
          <h2 className="text-xl font-bold text-foreground">Acessar Sistema</h2>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background border-border text-foreground focus:border-primary h-12 transition-all"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-background border-border text-foreground focus:border-primary h-12 transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-primary text-sm bg-primary/10 p-3 rounded-lg border border-primary/20">
                <AlertCircle size={16} />
                <p>{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isLoading}
              className="text-muted-foreground hover:text-foreground text-sm transition-colors disabled:opacity-50"
            >
              Esqueci minha senha
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
